/**
 * Missile smoke ribbon (SPEC §3.6.1, and the M6 readability requirement).
 *
 * The trail has to be visible at 8 km. A world-space ribbon a metre wide subtends about 0.007° at
 * that range — a fraction of a pixel, which the post stack's FXAA and block quantisation then
 * finish off entirely. So the width has a **screen-space floor**: the ribbon is expanded in view
 * space by whichever is larger, its true width or a fixed fraction of the viewport. Close in it
 * behaves like real geometry and reads as a column of smoke; far away it holds at a few pixels
 * and stays legible.
 *
 * That trade is deliberate and is the spec's instruction rather than an aesthetic choice:
 * "tune for readability first, appearance second".
 */

export const SMOKE_VERTEX = /* glsl */ `
attribute float aSide;
attribute float aAge;
attribute vec3 aTangent;

uniform float uWidthM;
uniform float uMinScreenWidth;
uniform float uLifetimeS;

varying float vFade;

void main() {
  vec4 viewPosition = modelViewMatrix * vec4(position, 1.0);

  // Smoke thins and spreads as it ages: older sections are wider and fainter.
  float age = clamp(aAge / uLifetimeS, 0.0, 1.0);
  vFade = 1.0 - age;

  // Screen-space floor on the width. -viewPosition.z is the distance down the view axis, so
  // scaling by it converts a viewport fraction into metres at this depth.
  float spread = 1.0 + age * 2.0;
  float widthM = max(uWidthM * spread, uMinScreenWidth * -viewPosition.z);

  // Expand perpendicular to the trail *and* to the view direction, which is what makes the ribbon
  // face the camera whatever angle it is seen from. Expanding along a fixed screen axis instead
  // collapses any trail running along that axis into a thin smear — a missile coming at you head
  // on would leave no visible smoke at all, which is precisely the case that matters most.
  vec3 tangentView = mat3(modelViewMatrix) * aTangent;
  float tangentLength = length(tangentView);
  vec3 toCamera = normalize(-viewPosition.xyz);
  vec3 side = tangentLength > 1e-5
    ? normalize(cross(tangentView / tangentLength, toCamera))
    : vec3(1.0, 0.0, 0.0);
  viewPosition.xyz += side * aSide * widthM;

  gl_Position = projectionMatrix * viewPosition;
}
`;

export const SMOKE_FRAGMENT = /* glsl */ `
precision highp float;

uniform vec3 uColour;
uniform float uOpacity;

varying float vFade;

void main() {
  // Squared falloff: the head of the trail stays dense while the tail dissolves, which is what
  // makes the direction of travel readable at a glance.
  float alpha = uOpacity * vFade * vFade;
  if (alpha < 0.004) discard;
  gl_FragColor = vec4(uColour, alpha);
}
`;
